
import { defineComponent } from '@vue/composition-api';
import { mapActions, mapGetters } from 'vuex';
import icons from '../components/icons.vue';
import {
  PrintTemplate,
  MarketForPrint,
  SelectOption,
  CategoryForPrint,
  TournamentForPrint
} from '../../../../types/sport';

interface SportListItem {
  id: number;
  count: number;
  name: string;
}

interface PrintOddsFormData {
  markets: string[];
  days: number;
  groupby: string;
  sport: string;
  category: number[];
  tournament: number[];
  sheet: number;
  orientation: 'landscape' | 'portrait';
}

export default defineComponent({
  name: 'IllinoisPrintOdds',

  components: {
    icons
  },

  data() {
    return {
      loading: false,
      printing: false,
      form: {
        markets: [],
        days: 1,
        groupby: 'day',
        sport: '',
        category: [],
        tournament: [],
        sheet: 9,
        orientation: 'landscape'
      } as PrintOddsFormData,
      sheets: [{ size: 'A4', value: 9 }] as Array<{
        size: string;
        value: number;
      }>,
      orientations: [] as SelectOption[],
      orderBy: [] as SelectOption[],
      orderBySelected: 'byTournaments' as string,
      sportSelected: null as number | null,
      sportCategories: [] as CategoryForPrint[],
      categoryTournament: [] as TournamentForPrint[],
      categorySelected: [] as number[],
      tournamentsSelected: [] as number[],
      sportMarketsMini: [] as MarketForPrint[],
      marketsSelected: [] as string[],
      managerPrintTemplates: [] as PrintTemplate[],
      skinPrintTemplates: [] as PrintTemplate[],
      totalNumberOutcomes: 0,
      maxNumberOfOutcomesAllowed: 37
    };
  },

  computed: {
    ...mapGetters({
      getLanguage: 'settings/getLanguage',
      getPlatformId: 'settings/getPlatformId',
      getTree: 'treeV2/getTree'
    }),

    listSports(): SportListItem[] {
      if (this.getTree && Object.keys(this.getTree).length > 0) {
        const filter = Object.values(this.getTree).filter(
          (el: any) => el.typeOdds === 'prematch'
        );
        return filter.map((el: any) => ({
          id: el.icon_id,
          count: el.count,
          name: el.name
        }));
      }
      return [];
    },

    availableCategories(): CategoryForPrint[] {
      return this.sportCategories.filter(
        cat => !this.categorySelected.includes(cat.id)
      );
    },

    availableTournaments(): TournamentForPrint[] {
      return this.categoryTournament.filter(
        tourn => !this.tournamentsSelected.includes(tourn.id)
      );
    },

    availableMarkets(): MarketForPrint[] {
      return this.sportMarketsMini.filter(
        market => !this.marketsSelected.includes(market.unique_id)
      );
    }
  },

  watch: {
    async sportSelected(newValue: number | null, oldValue: number | null) {
      if (newValue != null && newValue !== oldValue) {
        const response = await this.apiGetMarketsByPlatformSport({
          sportId: newValue,
          platformId: this.getPlatformId,
          language: this.getLanguage
        });
        this.sportMarketsMini = [];
        this.sportMarketsMini = response.data.result.markets || [];
        this.marketsSelected = [];

        const listCategories = await this.fetchCategorySport({
          sport: newValue
        });
        this.sportCategories = [];
        if (listCategories && Object.keys(listCategories).length > 0) {
          Object.values(listCategories).forEach((list: any) => {
            if (list.active) {
              const nameCat = list.detail.find(
                (d: any) => d.lang === this.getLanguage
              )?.text;
              if (nameCat) {
                this.sportCategories.push({ id: list.id, name: nameCat });
              }
            }
          });
        }
        this.sportCategories.sort((a, b) => a.name.localeCompare(b.name));
        this.categorySelected = [];
        this.tournamentsSelected = [];
      }
    },

    categorySelected: {
      async handler(newValue: number[]) {
        if (newValue && newValue.length > 0) {
          const listTournaments = await this.fetchTournamentByCategory({
            categories: JSON.stringify(newValue)
          });
          this.categoryTournament = [];
          if (listTournaments && Object.keys(listTournaments).length > 0) {
            Object.values(listTournaments).forEach((list: any) => {
              if (list.active) {
                const nameTour = list.detail.find(
                  (d: any) => d.lang === this.getLanguage
                )?.text;
                if (nameTour) {
                  this.categoryTournament.push({ id: list.id, name: nameTour });
                }
              }
            });
          }
        } else {
          this.categoryTournament = [];
          this.tournamentsSelected = [];
        }
      },
      deep: true
    },

    marketsSelected: {
      handler() {
        this.calculateTotalOutcomes();
      },
      deep: true
    }
  },

  methods: {
    ...mapActions({
      fetchCategorySport: 'tree/fetchCategorySport',
      fetchTournamentByCategory: 'tree/fetchTournamentByCategory',
      printOdds: 'odds/printOdds',
      fetchMyPrintTemplates: 'settings/fetchMyPrintTemplates',
      printTemplate: 'odds/printTemplate',
      apiGetMarketsByPlatformSport: 'marketview/apiGetMarketsByPlatformSport'
    }),

    calculateTotalOutcomes(): void {
      let total = 0;
      this.marketsSelected.forEach(selectedId => {
        const market = this.sportMarketsMini.find(
          m => m.unique_id === selectedId
        );
        if (market && market.numberOutcomes) {
          total += market.numberOutcomes;
        }
      });
      this.totalNumberOutcomes = total;
    },

    async printTemplateMethod(template: PrintTemplate): Promise<void> {
      const idTemplate = template._id;
      this.printing = true;
      this.$set(template, 'isPrinting', true);
      await this.printTemplate({ idTemplate: idTemplate });
      this.printing = false;
      this.$set(template, 'isPrinting', false);
    },

    async printSelectedMarkets(): Promise<void> {
      this.loading = true;

      if (this.sportSelected == null) {
        alert(this.$t('please_select_sport'));
        this.loading = false;
        return;
      }

      if (!this.marketsSelected || this.marketsSelected.length === 0) {
        alert(this.$t('please_select_markets'));
        this.loading = false;
        return;
      }

      if (this.form.days != null && this.form.days > 30) {
        alert(this.$t('insert_shorter_time_interval'));
        this.loading = false;
        return;
      }

      if (
        this.totalNumberOutcomes != null &&
        this.totalNumberOutcomes > this.maxNumberOfOutcomesAllowed
      ) {
        alert(
          this.$t('please_select_max_n_signs', {
            N: this.maxNumberOfOutcomesAllowed
          })
        );
        this.loading = false;
        return;
      }

      try {
        this.printing = true;
        const sportId = this.sportSelected;
        const categoriesIds = this.categorySelected;
        let tournamentsIds = this.tournamentsSelected;

        if (
          categoriesIds != null &&
          categoriesIds.length >= 2 &&
          tournamentsIds.length >= 0
        ) {
          this.tournamentsSelected = [];
          tournamentsIds = this.tournamentsSelected;
        }

        const response = await this.printOdds({
          platformId: this.getPlatformId,
          sportId: sportId,
          categoriesIds: categoriesIds,
          tournamentsIds: tournamentsIds,
          marketsUniqueIds: this.marketsSelected,
          days: this.form.days,
          groupBy: this.orderBySelected,
          orientation: this.form.orientation,
          language: this.getLanguage,
          sheetType: this.form.sheet
        });

        if (response == null) {
          alert(this.$t('generic.error'));
        }
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
        this.printing = false;
      }
    },

    getCategoryName(catId: number): string {
      const cat = this.sportCategories.find(c => c.id === catId);
      return cat ? cat.name : String(catId);
    },

    getTournamentName(tournId: number): string {
      const tourn = this.categoryTournament.find(t => t.id === tournId);
      return tourn ? tourn.name : String(tournId);
    },

    getMarketName(marketId: string): string {
      const market = this.sportMarketsMini.find(m => m.unique_id === marketId);
      return market ? market.nameWithNumberOutcomes : marketId;
    },

    addCategory(event: Event): void {
      const target = event.target as HTMLSelectElement;
      const value = parseInt(target.value, 10);
      if (value && !this.categorySelected.includes(value)) {
        this.categorySelected.push(value);
      }
      target.value = '';
    },

    removeCategory(catId: number): void {
      const index = this.categorySelected.indexOf(catId);
      if (index > -1) {
        this.categorySelected.splice(index, 1);
      }
    },

    addTournament(event: Event): void {
      const target = event.target as HTMLSelectElement;
      const value = parseInt(target.value, 10);
      if (value && !this.tournamentsSelected.includes(value)) {
        this.tournamentsSelected.push(value);
      }
      target.value = '';
    },

    removeTournament(tournId: number): void {
      const index = this.tournamentsSelected.indexOf(tournId);
      if (index > -1) {
        this.tournamentsSelected.splice(index, 1);
      }
    },

    addMarket(event: Event): void {
      const target = event.target as HTMLSelectElement;
      const value = target.value;
      if (value && !this.marketsSelected.includes(value)) {
        this.marketsSelected.push(value);
      }
      target.value = '';
    },

    removeMarket(marketId: string): void {
      const index = this.marketsSelected.indexOf(marketId);
      if (index > -1) {
        this.marketsSelected.splice(index, 1);
      }
    }
  },

  async mounted() {
    const responsePrintTemplate = await this.fetchMyPrintTemplates(
      this.getPlatformId
    );

    try {
      this.managerPrintTemplates =
        responsePrintTemplate.managerPrintTemplates || [];
      this.skinPrintTemplates = responsePrintTemplate.printTemplates || [];
    } catch (e) {
      console.log(e);
    }

    this.orientations = [
      { name: this.$t('horizontal') as string, value: 'landscape' },
      { name: this.$t('vertical') as string, value: 'portrait' }
    ];

    this.orderBy = [
      { name: this.$t('byTournaments') as string, value: 'byTournaments' }
    ];

    // Seleziona automaticamente il primo sport se disponibile
    if (this.listSports.length > 0 && !this.sportSelected) {
      this.sportSelected = this.listSports[0].id;
    }
  }
});
